import { useEffect, useState } from 'react';

import { canSpendProactive } from '@bookora/shared';

import { useAuth } from '@/auth/token-store';

import { useGuidance } from './api';
import { notifyProactiveSurface, useGuidanceBusy } from './runtime';
import { noteProactiveShown, readLastOffer, tabGuardAllows } from './storage';

/**
 * One proactive surface per budget: tours, the smart guide, the welcome and
 * an offer modal all spend the same `canSpendProactive`. Returns true once —
 * and stays true for the life of the component — after the slot was granted
 * and spent, so the caller opens its surface exactly then.
 *
 * Never while another guidance surface is open (`useGuidanceBusy`), while
 * impersonating, or in a read-only clinic.
 */
export function useProactiveSlot(want: boolean): boolean {
  const busy = useGuidanceBusy();
  const guidance = useGuidance();
  const { impersonatedBy } = useAuth();
  const [granted, setGranted] = useState(false);
  const data = guidance.data;

  useEffect(() => {
    if (!want || granted || busy || impersonatedBy || !data || data.readOnly) return;
    const now = new Date();
    if (!tabGuardAllows(now)) return;
    const profile = {
      ...data.profile,
      autoOfferPausedUntil: data.profile.autoOfferPausedUntil ?? null,
      lastProactiveAt: data.profile.lastProactiveAt ?? null,
      proactiveDayKey: data.profile.proactiveDayKey ?? null,
      tipsPausedUntil: data.profile.tipsPausedUntil ?? null,
      newTipsDayKey: data.profile.newTipsDayKey ?? null,
      tipBackoffUntil: data.profile.tipBackoffUntil ?? null,
    };
    if (!canSpendProactive(profile, now, { localLastAt: readLastOffer() })) return;
    noteProactiveShown(now);
    notifyProactiveSurface();
    setGranted(true);
  }, [want, granted, busy, impersonatedBy, data]);

  return granted;
}
